import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useSession, SessionProvider, isSessionValid } from './hooks/useSession';
import Sidebar from './sidebar';
import Login from './login';
import { UserProvider } from './contexts/UserContext';
import { IconResolverProvider } from './contexts/IconResolverContext';
import { MenuAccessProvider } from './contexts/MenuAccessContext';
import MenuRouteGuard from './components/MenuRouteGuard';
import './App.css';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from '@react-oauth/google';
import { getSafeReturnUrl } from './utils/returnUrl';

// 動的インポートでコンポーネントを遅延読み込み
const DeliveryQR = lazy(() => import("./delivery_qr"));
const DeliveryStatusView = lazy(() => import("./delivery_status_view"));
const DeliveryMap = lazy(() => import("./delivery_map"));
const IssueTracker = lazy(() => import("./issue_tracker"));
const FDAInvoiceEntry = lazy(() => import("./fda_invoice_entry"));
const FDAInvoicePhoto = lazy(() => import("./fda_invoice_photo"));
const ConsignmentDeliveryView = lazy(() => import("./consignment_delivery_view"));
const ConsignmentCustomerStatus = lazy(() => import("./consignment_customer_status"));
const ConsignmentDeliveryHistory = lazy(() => import("./consignment_delivery_history"));
const ConsignmentItemMaster = lazy(() => import("./consignment_item_master"));
const FDAMasterImport = lazy(() => import("./fda_master_import"));
const MasterImport = lazy(() => import("./master_import"));
const OrderImport = lazy(() => import("./order_import"));
const SalesImport = lazy(() => import("./sales_import"));
const UserImport = lazy(() => import("./user_import"));
const ItemCatalog = lazy(() => import("./item_catalog"));
const ItemList = lazy(() => import("./item_list"));
const BookingEntry = lazy(() => import("./booking_entry"));
const BookingHistory = lazy(() => import("./booking_history"));
const ConsignmentReports = lazy(() => import("./consignment_reports"));
const ConsignmentSalesAnalysis = lazy(() => import("./consignment_sales_analysis"));
const ConsignmentNoStockEntry = lazy(() => import("./consignment_no_stock_entry"))
const StockImport = lazy(() => import("./stock_import"));
const MyCustomer = lazy(() => import("./my_customer"));
const ConversionMaster = lazy(() => import("./conversion_master"));
const OrderEntry = lazy(() => import("./order_entry"));
const EcOrderOutput = lazy(() => import("./ec_order_output"));
const OrderInquiry = lazy(() => import("./order_inquiry"));
const ItemSearch = lazy(() => import("./item_search"));
const StopDelivery = lazy(() => import("./stop_delivery"));
const PurchaseOrderSheet = lazy(() => import("./purchase_order_sheet"));
const SalesHistory = lazy(() => import("./sales_history"));
const FunctionMaster = lazy(() => import("./function_master"));
const MenuMaster = lazy(() => import("./menu_master"));
const EventMaster = lazy(() => import("./event_master"));
const MakerMaster = lazy(() => import("./maker_master"));
const CalendarMaster = lazy(() => import("./calendar_master"));
const CustomerUserMaster = lazy(() => import("./customer_user_master"));
const EventOrder = lazy(() => import("./event_order"));
const EventOrderRanking = lazy(() => import("./event_order_ranking"));
const EventOrderAllocation = lazy(() => import("./event_order_allacation"));
const EventAttendeeRegistration = lazy(() => import("./event_attendee_registration"));
const EventAttendeeStatus = lazy(() => import("./event_attendee_status"));
const EventCustomerSummary = lazy(() => import("./event_customer_summary"));
const NewItemApplication = lazy(() => import("./new_item_application"));
const Dashboard = lazy(() => import("./dashboard/Dashboard"));
const ConsignmentActiveCheckPage = lazy(() => import("./components/consignment_active_check"));
const ConsignmentStatusPage = lazy(() => import("./consignment_status"));
const TeamMaster = lazy(() => import("./team_master"));
const Announcements = lazy(() => import("./announcements"));
const AnnouncementDetail = lazy(() => import("./announcement_detail"));
const AnnouncementAdmin = lazy(() => import("./announcement_admin"));
const LineMessage = lazy(() => import("./line_message"));
const TaskBoard = lazy(() => import("./task_board"));

const queryClient = new QueryClient();

// ローディングコンポーネント
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useSession();
  const location = useLocation();
  if (isAuthenticated && isSessionValid()) return children;
  return <Navigate to="/login" state={{ from: location }} replace />;
}

function LoginRedirect() {
  const location = useLocation();
  return <Navigate to={getSafeReturnUrl(location.state?.from)} replace />;
}

function DashboardRoutes() {
  return (
    <ProtectedRoute>
      <IconResolverProvider>
        <MenuAccessProvider>
          <Sidebar>
            <MenuRouteGuard>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
              <Route path="/" element={<Dashboard />} /> 
              <Route path="/consignment-delivery" element={<ConsignmentDeliveryView />} />
              <Route path="/consignment-delivery-history" element={<ConsignmentDeliveryHistory />} />
              <Route path="/consignment-customer-status" element={<ConsignmentCustomerStatus />} />
              <Route path="/consignment-active-check" element={<ConsignmentActiveCheckPage />} />
              <Route path="/consignment-status" element={<ConsignmentStatusPage />} />
              <Route path="/consignment-reports" element={<ConsignmentReports />} />
              <Route path="/consignment-sales-analysis" element={<ConsignmentSalesAnalysis />} />
              <Route path="/consignment-no-stock-entry" element={<ConsignmentNoStockEntry />} />
              <Route path="/stock-import" element={<StockImport />} />
              <Route path="/item-search" element={<ItemSearch />} />
              <Route path="/stop-delivery" element={<StopDelivery />} />
              <Route path="/delivery-qr" element={<DeliveryQR />} />
              <Route path="/delivery-status" element={<DeliveryStatusView />} />
              <Route path="/delivery-map" element={<DeliveryMap />} />
              <Route path="/issue-tracker" element={<IssueTracker />} />
              <Route path="/consignment-item-master" element={<ConsignmentItemMaster />} />
              <Route path="/fda-invoice-entry" element={<FDAInvoiceEntry />} />
              <Route path="/fda-invoice-photo" element={<FDAInvoicePhoto />} />
              <Route path="/fda-master-import" element={<FDAMasterImport />} />
              <Route path="/master-import" element={<MasterImport />} />
              <Route path="/order-import" element={<OrderImport />} />
              <Route path="/sales-import" element={<SalesImport />} />
              <Route path="/user-import" element={<UserImport />} />
              <Route path="/item-catalog" element={<ItemCatalog />} />
              <Route path="/item-list" element={<ItemList />} />
              <Route path="/booking-entry" element={<BookingEntry />} />
              <Route path="/booking-history" element={<BookingHistory />} />
              <Route path="/my-customer" element={<MyCustomer />} />
              <Route path="/conversion-master" element={<ConversionMaster />} />
              <Route path="/order-entry" element={<OrderEntry />} />
              <Route path="/ec-order-output" element={<EcOrderOutput />} />
              <Route path="/order-inquiry" element={<OrderInquiry />} />
              <Route path="/purchase-order-sheet" element={<PurchaseOrderSheet />} />
              <Route path="/sales-history" element={<SalesHistory />} />
              <Route path="/function-master" element={<FunctionMaster />} />
              <Route path="/menu-master" element={<MenuMaster />} />
              <Route path="/event-master" element={<EventMaster />} />
              <Route path="/maker-master" element={<MakerMaster />} />
              <Route path="/calendar-master" element={<CalendarMaster />} />
              <Route path="/customer-user-master" element={<CustomerUserMaster />} />
              <Route path="/event-order" element={<EventOrder />} />
              <Route path="/event-order-ranking" element={<EventOrderRanking />} />
              <Route path="/event-order-allocation" element={<EventOrderAllocation />} />
              <Route path="/event-attendee-registration" element={<EventAttendeeRegistration />} />
              <Route path="/event-attendee-status" element={<EventAttendeeStatus />} />
              <Route path="/event-customer-summary" element={<EventCustomerSummary />} />
              <Route path="/new-item-application" element={<NewItemApplication />} />
              <Route path="/team-master" element={<TeamMaster />} />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/announcements/:id" element={<AnnouncementDetail />} />
              <Route path="/announcement-admin" element={<AnnouncementAdmin />} />
              <Route path="/line-message" element={<LineMessage />} />
              <Route path="/task-board" element={<TaskBoard />} />
              <Route path="/location" element={""} />
              <Route path="/user" element={""} />
              <Route path="/item" element={""} />
            </Routes>
              </Suspense>
            </MenuRouteGuard>
          </Sidebar>
        </MenuAccessProvider>
      </IconResolverProvider>
    </ProtectedRoute>
  );
}

function App() {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  console.log("GoogleOAuthProvider clientId:", clientId);
  return (
    <GoogleOAuthProvider clientId={clientId}>
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <Router>
            <SessionProvider>
              <AppRoutes />
            </SessionProvider>
          </Router>
        </UserProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

function TaskBoardMailLinkRedirect() {
  const location = useLocation();
  return <Navigate to={`/dashboard/task-board${location.search || ""}`} replace />;
}

function AppRoutes() {
  const { isAuthenticated } = useSession();
  const authenticated = isAuthenticated && isSessionValid();

  return (
    <Routes>
      <Route path="/login" element={authenticated ? <LoginRedirect /> : <Login />} />
      <Route path="/task-board" element={<TaskBoardMailLinkRedirect />} />
      <Route path="/dashboard/*" element={<DashboardRoutes />} />
      <Route path="/" element={authenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
